import TableFooter from '@mui/material/TableFooter'
import Typography from '@mui/material/Typography'
import TableBody from '@mui/material/TableBody'
import { useTranslation } from 'react-i18next'
import TableRow from '@mui/material/TableRow'
import Table from '@mui/material/Table'
import Box from '@mui/material/Box'
import React from 'react'

import { StyledTableCell, StyledTableHead } from 'shared/components/StyledTableComponents'
import RadiusTableWrapper from 'shared/components/RadiusTableWrapper'

import { ProductType } from '../../types'

interface Props {
   data: ProductType[]
   formattedNetValue: string
   formattedGrossValue: string
}

export default function OrderedTable({ data, formattedGrossValue, formattedNetValue }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'ORDER_SUMMARY'])

   function returnComment(name: PropType<ProductType, 'status'>) {
      switch (name) {
         case 'NOT_AVAILABLE':
            return t('ORDER_SUMMARY:tables.notAvailable')
         case 'PRICE_HAS_CHANGED':
            return t('ORDER_SUMMARY:tables.priceHasChanged')
         case 'WHOLESALE_CONNECTION_PROBLEM':
            return t('ORDER_SUMMARY:tables.wholesaleConnectionProblem')
         case 'TOO_LOW_AMOUNT':
            return t('ORDER_SUMMARY:tables.tooLowAmount')
         case 'DELIVERY_PROBLEM':
            return t('ORDER_SUMMARY:tables.deliveryProblem')
         case 'INDEX_PROBLEM':
            return t('ORDER_SUMMARY:tables.indexProblem')
         case 'PAYMENTS_PROBLEM':
            return t('ORDER_SUMMARY:tables.paymentsProblem')
         case 'WHOLESALE_ERROR':
            return t('ORDER_SUMMARY:tables.wholesaleError')
         case 'PARTIALLY_AVAILABLE':
            return t('ORDER_SUMMARY:tables.partiallyAvailable')
         default:
            return null
      }
   }

   function returnWholesaleErrorMessage(
      status: PropType<OrderProductType, 'status'>,
      wholesaleErrorMessage?: Nullable<string>
   ) {
      if (status !== 'AVAILABLE' && status !== 'PARTIALLY_AVAILABLE') {
         if (!!wholesaleErrorMessage && !wholesaleErrorMessage?.includes('NOT_HANDLED')) {
            return wholesaleErrorMessage
         }
         if (status === 'PRICE_HAS_CHANGED') {
            return t('ORDER_SUMMARY:tables.priceHasChanges')
         }
         if (status === 'TOO_LOW_AMOUNT') {
            return t('ORDER_SUMMARY:tables.tooLowAmount')
         }
         return t('ORDER_SUMMARY:tables.wholesaleNoResponse')
      }
      return ''
   }

   const notOrderedProducts = data.filter((item) => item.status !== 'AVAILABLE').length

   return (
      <Box>
         <Typography variant="body2" color="text.secondary">
            {t('ORDER_SUMMARY:tables.orderedProducts')}
         </Typography>
         <RadiusTableWrapper>
            <Table sx={{ border: '1px solid rgba(224, 224, 224, 1)', width: 1200 }}>
               <StyledTableHead>
                  <TableRow>
                     <StyledTableCell>{t('TRANSLATION:columnNames.partIndex')}</StyledTableCell>
                     <StyledTableCell>{t('ORDER_SUMMARY:tables.orderNumber')}</StyledTableCell>
                     <StyledTableCell>{t('ORDER_SUMMARY:tables.externalProductId')}</StyledTableCell>
                     <StyledTableCell>{t('TRANSLATION:columnNames.wholesaler')}</StyledTableCell>
                     <StyledTableCell>{t('TRANSLATION:columnNames.quantity')}</StyledTableCell>
                     <StyledTableCell>{t('TRANSLATION:columnNames.netPrice')}</StyledTableCell>
                     <StyledTableCell>VAT</StyledTableCell>
                     <StyledTableCell>{t('TRANSLATION:columnNames.grossValue')}</StyledTableCell>
                     {!!notOrderedProducts && (
                        <>
                           <StyledTableCell>{t('TRANSLATION:columnNames.description')}</StyledTableCell>
                           <StyledTableCell>{t('ORDER_SUMMARY:tables.wholesaleInfo')}</StyledTableCell>
                        </>
                     )}
                  </TableRow>
               </StyledTableHead>
               <TableBody>
                  {data.map((item) => (
                     <TableRow
                        sx={{ backgroundColor: item.status !== 'AVAILABLE' ? 'rgba(255,0,0,0.4)' : null }}
                        key={item.productUuid}
                     >
                        <StyledTableCell>{item.index}</StyledTableCell>
                        <StyledTableCell>{item.wholesaleOrderNumber}</StyledTableCell>
                        <StyledTableCell>{item.externalProductId}</StyledTableCell>
                        <StyledTableCell>
                           <img
                              height="20px"
                              width="auto"
                              src={`${process.env.REACT_APP_FILES_URL}/img/logos-wholesalers/light/${item.wholesalesUuid}.png`}
                              alt={item.wholesaleName}
                           />
                        </StyledTableCell>
                        <StyledTableCell>{item.amount}</StyledTableCell>
                        <StyledTableCell>{item.formattedPrice}</StyledTableCell>
                        <StyledTableCell>{item.taxRate}%</StyledTableCell>
                        <StyledTableCell>{item.formattedGrossValue} </StyledTableCell>
                        {!!notOrderedProducts && (
                           <>
                              <StyledTableCell>
                                 {item.status !== 'AVAILABLE' && returnComment(item.status)}
                              </StyledTableCell>
                              <StyledTableCell>
                                 {item.status !== 'AVAILABLE' &&
                                    returnWholesaleErrorMessage(item.status, item.wholesaleErrorMessage)}
                              </StyledTableCell>
                           </>
                        )}
                     </TableRow>
                  ))}
               </TableBody>
               <TableFooter>
                  <TableRow sx={{ backgroundColor: 'rgba(33,147,224, 0.1)' }}>
                     <StyledTableCell sx={{ fontWeight: 'bold', fontSize: 14 }} colSpan={4}>
                        {`${t('TRANSLATION:columnNames.netSum')}: ${formattedNetValue}`}
                     </StyledTableCell>
                     <StyledTableCell sx={{ fontWeight: 'bold', fontSize: 14 }} colSpan={5}>
                        {`${t('TRANSLATION:columnNames.grossSum')}: ${formattedGrossValue}`}
                     </StyledTableCell>
                  </TableRow>
               </TableFooter>
            </Table>
         </RadiusTableWrapper>
         {!!data.filter((item) => item.status !== 'AVAILABLE').length && (
            <Typography variant="body2">{t('ORDER_SUMMARY:notOrderedProducts')}</Typography>
         )}
      </Box>
   )
}
